/**
 * Hojas de etiquetas de artículos: arma las figuras de cada página en puntos
 * (1/72"), con el origen arriba a la izquierda. La vista previa (SVG) y el PDF
 * dibujan las mismas figuras, así que lo que se ve es lo que sale impreso.
 */

import { zlibSync } from 'fflate'

import { articleBarcode, type Barcode } from './barcode'

export type FontName = 'regular' | 'bold' | 'mono'

export type Shape =
  | { kind: 'bar'; x: number; y: number; w: number; h: number }
  | { kind: 'text'; x: number; y: number; text: string; size: number; font: FontName; align: 'left' | 'center' | 'right' }
  /** Línea de corte punteada: se imprime. */
  | { kind: 'cut'; x: number; y: number; w: number; h: number }
  /** Borde de la etiqueta adhesiva: solo en la vista previa. */
  | { kind: 'outline'; x: number; y: number; w: number; h: number }

export interface LabelItem {
  code: string
  description: string
  price: number | null
}

export type SheetFormat = 'roll50x26' | 'roll35x26' | 'roll50x50' | 'avery5160' | 'counter'

/** Rollos de la impresora térmica: una etiqueta por página, del tamaño exacto. */
export const ROLL_FORMATS: readonly SheetFormat[] = ['roll50x26', 'roll35x26', 'roll50x50']

export function isRoll(format: SheetFormat): boolean {
  return ROLL_FORMATS.includes(format)
}

export interface SheetOptions {
  format: SheetFormat
  businessName: string
  /** Etiquetas ya usadas en la primera hoja (solo adhesivas). */
  skip: number
}

export const PAGE_WIDTH = 612
export const PAGE_HEIGHT = 792

export interface PageSize {
  width: number
  height: number
}

const mm = (value: number) => (value * 72) / 25.4

const num = (n: number) => (Math.round(n * 1000) / 1000).toString()

interface Grid {
  cols: number
  rows: number
  left: number
  top: number
  width: number
  height: number
  pitchX: number
  pitchY: number
}

// Avery 5160 / 8160: carta, 3 × 10 de 2 5/8" × 1", 3/16" de margen lateral y 1/2" arriba.
const AVERY_5160: Grid = { cols: 3, rows: 10, left: 13.5, top: 36, width: 189, height: 72, pitchX: 198, pitchY: 72 }
const COUNTER_MARGIN = 43.2
const COUNTER_TOP = COUNTER_MARGIN + 34
const COUNTER: Grid = {
  cols: 2,
  rows: 4,
  left: COUNTER_MARGIN,
  top: COUNTER_TOP,
  width: (PAGE_WIDTH - 2 * COUNTER_MARGIN) / 2,
  height: (PAGE_HEIGHT - COUNTER_TOP - COUNTER_MARGIN) / 4,
  pitchX: (PAGE_WIDTH - 2 * COUNTER_MARGIN) / 2,
  pitchY: (PAGE_HEIGHT - COUNTER_TOP - COUNTER_MARGIN) / 4,
}

const PAGE_SIZES: Record<SheetFormat, PageSize> = {
  roll50x26: { width: mm(50), height: mm(26) },
  roll35x26: { width: mm(35), height: mm(26) },
  roll50x50: { width: mm(50), height: mm(50) },
  avery5160: { width: PAGE_WIDTH, height: PAGE_HEIGHT },
  counter: { width: PAGE_WIDTH, height: PAGE_HEIGHT },
}

export function pageSize(format: SheetFormat): PageSize {
  return PAGE_SIZES[format]
}

const rollGrid = (format: SheetFormat): Grid => {
  const { width, height } = PAGE_SIZES[format]
  return { cols: 1, rows: 1, left: 0, top: 0, width, height, pitchX: width, pitchY: height }
}

const GRIDS: Record<SheetFormat, Grid> = {
  roll50x26: rollGrid('roll50x26'),
  roll35x26: rollGrid('roll35x26'),
  roll50x50: rollGrid('roll50x50'),
  avery5160: AVERY_5160,
  counter: COUNTER,
}

export const LABELS_PER_PAGE: Record<SheetFormat, number> = {
  roll50x26: 1,
  roll35x26: 1,
  roll50x50: 1,
  avery5160: 30,
  counter: 8,
}

// Anchos de Helvetica y Helvetica-Bold (AFM, milésimas del cuerpo) para los caracteres 32 a 126.
const HELVETICA = (
  '278 278 355 556 556 889 667 191 333 333 389 584 278 333 278 278 556 556 556 556 556 556 556 556 ' +
  '556 556 278 278 584 584 584 556 1015 667 667 722 722 667 611 778 722 278 500 667 556 833 722 778 ' +
  '667 778 722 667 611 722 667 944 667 667 611 278 278 278 469 556 333 556 556 500 556 556 278 556 ' +
  '556 222 222 500 222 833 556 556 556 556 333 500 278 556 500 722 500 500 500 334 260 334 584'
).split(' ').map(Number)
const HELVETICA_BOLD = (
  '278 333 474 556 556 889 722 238 333 333 389 584 278 333 278 278 556 556 556 556 556 556 556 556 ' +
  '556 556 333 333 584 584 584 611 975 722 722 722 722 667 611 778 722 278 556 722 611 833 722 778 ' +
  '667 778 722 667 611 722 667 944 667 667 611 333 278 333 584 556 333 556 611 556 611 556 333 611 ' +
  '611 278 278 556 278 889 611 611 611 611 389 556 333 611 556 778 556 556 500 389 280 389 584'
).split(' ').map(Number)

function charWidth(ch: string, font: FontName): number {
  if (font === 'mono') return 600
  const table = font === 'bold' ? HELVETICA_BOLD : HELVETICA
  if (ch === '…') return 1000
  // Las letras con tilde miden lo mismo que la letra sin ella.
  const base = ch.normalize('NFD')[0]
  const code = base.charCodeAt(0)
  return code >= 32 && code <= 126 ? table[code - 32] : 556
}

export function textWidth(text: string, font: FontName, size: number): number {
  let total = 0
  for (const ch of text) total += charWidth(ch, font)
  return (total * size) / 1000
}

/** Recorta con "…" lo que no cabe en el ancho. */
export function fitText(text: string, font: FontName, size: number, maxWidth: number): string {
  const clean = text.replace(/\s+/g, ' ').trim()
  if (textWidth(clean, font, size) <= maxWidth) return clean
  let cut = clean
  while (cut && textWidth(cut + '…', font, size) > maxWidth) cut = cut.slice(0, -1)
  return cut.trimEnd() + '…'
}

/**
 * Parte el texto en renglones de hasta `maxWidth`, cortando entre palabras.
 * Lo que no entra en `maxLines` se recorta con "…" en el último renglón.
 */
export function wrapText(text: string, font: FontName, size: number, maxWidth: number, maxLines: number): string[] {
  const words = text.replace(/\s+/g, ' ').trim().split(' ')
  const lines: string[] = []
  let i = 0
  while (i < words.length && lines.length < maxLines) {
    if (lines.length === maxLines - 1) {
      lines.push(fitText(words.slice(i).join(' '), font, size, maxWidth))
      break
    }
    let line = words[i++]
    while (i < words.length && textWidth(`${line} ${words[i]}`, font, size) <= maxWidth) line += ` ${words[i++]}`
    lines.push(fitText(line, font, size, maxWidth))
  }
  return lines
}

/**
 * El colón (₡) no existe en las fuentes estándar del PDF; se usa ¢, que es
 * como se escribe a mano en los rótulos de precio.
 */
export function formatLabelPrice(amount: number): string {
  const rounded = Math.round(amount * 100) / 100
  const digits = rounded.toLocaleString('es-CR', {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  })
  return '¢' + digits.replace(/\s/g, ' ')
}

function drawBarcode(shapes: Shape[], barcode: Barcode, centerX: number, top: number, module: number, height: number, guardExtra: number) {
  const total = barcode.quietLeft + barcode.modules.length + barcode.quietRight
  const x = centerX - (total * module) / 2 + barcode.quietLeft * module
  let i = 0
  while (i < barcode.modules.length) {
    if (barcode.modules[i] !== '1') {
      i++
      continue
    }
    // Las barras contiguas van en un solo rectángulo: sin costuras al imprimir.
    let j = i
    while (j < barcode.modules.length && barcode.modules[j] === '1' && barcode.guards.has(j) === barcode.guards.has(i)) j++
    const extra = barcode.guards.has(i) ? guardExtra : 0
    shapes.push({ kind: 'bar', x: x + i * module, y: top, w: (j - i) * module, h: height + extra })
    i = j
  }
}

/** Centro de las barras: el EAN-13 deja más blanco a la izquierda que a la derecha. */
function barsCenter(barcode: Barcode, centerX: number, module: number): number {
  return centerX + ((barcode.quietLeft - barcode.quietRight) * module) / 2
}

/** Ancho del nombre: el de las barras, para que quede alineado con ellas. */
function nameWidth(barcode: Barcode, module: number, minimum: number): number {
  return Math.max(barcode.modules.length * module, minimum)
}

function moduleFor(barcode: Barcode, preferred: number, maxWidth: number): number {
  const total = barcode.quietLeft + barcode.modules.length + barcode.quietRight
  return Math.min(preferred, maxWidth / total)
}

// 0,25 mm: con barras más finas una impresora de 203 o 300 dpi ya no las imprime parejas.
const MIN_MODULE = 0.7

/** Margen lateral: en el rollo, la impresora puede correr la etiqueta 1 mm. */
function sidePad(format: SheetFormat): number {
  return isRoll(format) ? 6 : 8
}

function barcodeWidth(format: SheetFormat): number {
  return format === 'counter' ? COUNTER.width - 40 : GRIDS[format].width - 2 * sidePad(format)
}

/** Si el código cabe en la etiqueta con barras que un lector pueda leer. */
export function fitsLabel(barcode: Barcode, format: SheetFormat): boolean {
  const preferred = format === 'counter' ? 1.417 : 1
  return moduleFor(barcode, preferred, barcodeWidth(format)) >= MIN_MODULE
}

/** Etiqueta de 1" de alto: hoja Avery y rollos de 26 mm. */
function compactLabel(shapes: Shape[], item: LabelItem, barcode: Barcode, x: number, y: number, opts: SheetOptions) {
  const { width } = GRIDS[opts.format]
  const pad = sidePad(opts.format)
  const inner = barcodeWidth(opts.format)
  const cx = x + width / 2
  if (item.price !== null) {
    const price = formatLabelPrice(item.price)
    const priceWidth = textWidth(price, 'bold', 8)
    shapes.push({ kind: 'text', x: x + width - pad, y: y + 10.5, text: price, size: 8, font: 'bold', align: 'right' })
    const name = fitText(opts.businessName.toUpperCase(), 'regular', 6, inner - priceWidth - 6)
    shapes.push({ kind: 'text', x: x + pad, y: y + 10, text: name, size: 6, font: 'regular', align: 'left' })
  } else {
    const name = fitText(opts.businessName.toUpperCase(), 'regular', 6, inner)
    shapes.push({ kind: 'text', x: cx, y: y + 10, text: name, size: 6, font: 'regular', align: 'center' })
  }
  const module = moduleFor(barcode, 1, inner)
  const lines = wrapText(item.description, 'bold', 7.5, nameWidth(barcode, module, Math.min(95, inner)), 2)
  const bx = barsCenter(barcode, cx, module)
  lines.forEach((line, i) => {
    shapes.push({ kind: 'text', x: bx, y: y + (lines.length === 1 ? 21 : 19 + i * 8), text: line, size: 7.5, font: 'bold', align: 'center' })
  })
  // Con dos renglones las barras ceden alto: la etiqueta mide 1".
  const barsTop = lines.length === 1 ? 25 : 30
  drawBarcode(shapes, barcode, cx, y + barsTop, module, 57 - barsTop, 3)
  shapes.push({ kind: 'text', x: bx, y: y + 67, text: barcode.text, size: 7, font: 'mono', align: 'center' })
}

/** Rollo de 5 × 5 cm: hay alto para letra más grande y el precio abajo. */
function squareLabel(shapes: Shape[], item: LabelItem, barcode: Barcode, x: number, y: number, opts: SheetOptions) {
  const { width, height } = GRIDS[opts.format]
  const cx = x + width / 2
  const module = moduleFor(barcode, 1, barcodeWidth(opts.format))
  const bx = barsCenter(barcode, cx, module)
  const lines = wrapText(item.description, 'bold', 9, nameWidth(barcode, module, 100), 3)
  const barsHeight = 44
  const blockHeight = 10 + lines.length * 10.5 + 3 + barsHeight + 3 + 11 + (item.price !== null ? 16 : 0)
  let cursor = y + (height - blockHeight) / 2
  const business = fitText(opts.businessName.toUpperCase(), 'regular', 7, width - 12)
  shapes.push({ kind: 'text', x: cx, y: cursor + 7, text: business, size: 7, font: 'regular', align: 'center' })
  cursor += 10
  for (const line of lines) {
    shapes.push({ kind: 'text', x: bx, y: cursor + 9, text: line, size: 9, font: 'bold', align: 'center' })
    cursor += 10.5
  }
  cursor += 3
  drawBarcode(shapes, barcode, cx, cursor, module, barsHeight, 3)
  cursor += barsHeight + 3
  shapes.push({ kind: 'text', x: bx, y: cursor + 9, text: barcode.text, size: 8, font: 'mono', align: 'center' })
  cursor += 11
  if (item.price !== null) {
    const price = formatLabelPrice(item.price)
    shapes.push({ kind: 'text', x: bx, y: cursor + 14, text: price, size: 14, font: 'bold', align: 'center' })
  }
}

function counterLabel(shapes: Shape[], item: LabelItem, barcode: Barcode, x: number, y: number) {
  const { width, height } = COUNTER
  const cx = x + width / 2
  shapes.push({ kind: 'cut', x: x + 6, y: y + 6, w: width - 12, h: height - 12 })
  const module = moduleFor(barcode, 1.417, width - 40)
  const barsHeight = 62
  const barsTop = y + (height - barsHeight) / 2 - (item.price !== null ? 6 : 0)
  const lines = wrapText(item.description, 'bold', 12, nameWidth(barcode, module, 134), 2)
  const bx = barsCenter(barcode, cx, module)
  lines.forEach((line, i) => {
    shapes.push({ kind: 'text', x: bx, y: barsTop - 10 - (lines.length - 1 - i) * 14, text: line, size: 12, font: 'bold', align: 'center' })
  })
  drawBarcode(shapes, barcode, cx, barsTop, module, barsHeight, 5.7)
  shapes.push({ kind: 'text', x: bx, y: barsTop + barsHeight + 20, text: barcode.text, size: 13, font: 'mono', align: 'center' })
  if (item.price !== null) {
    shapes.push({ kind: 'text', x: bx, y: barsTop + barsHeight + 36, text: formatLabelPrice(item.price), size: 12, font: 'bold', align: 'center' })
  }
}

/**
 * Páginas de la hoja. Lanza si un código no se puede imprimir (solo pasa con
 * caracteres fuera de ASCII); la pantalla lo filtra antes con `articleBarcode`.
 */
export function buildSheet(items: LabelItem[], opts: SheetOptions, header: { title: string; hint: string }): Shape[][] {
  const grid = GRIDS[opts.format]
  const perPage = grid.cols * grid.rows
  const skip = opts.format === 'avery5160' ? Math.max(0, Math.min(opts.skip, perPage - 1)) : 0
  const pages: Shape[][] = []
  // Las copias de un mismo artículo comparten el código: se arma una vez.
  const barcodes = new Map<string, Barcode | null>()
  items.forEach((item, index) => {
    const slot = index + skip
    const pageIndex = Math.floor(slot / perPage)
    if (!pages[pageIndex]) {
      const page: Shape[] = []
      if (opts.format === 'counter') {
        page.push({ kind: 'text', x: COUNTER_MARGIN, y: COUNTER_MARGIN, text: header.title, size: 16, font: 'bold', align: 'left' })
        page.push({ kind: 'text', x: COUNTER_MARGIN, y: COUNTER_MARGIN + 14, text: header.hint, size: 9, font: 'regular', align: 'left' })
      }
      pages[pageIndex] = page
    }
    const position = slot % perPage
    const row = Math.floor(position / grid.cols)
    const col = position % grid.cols
    const x = grid.left + col * grid.pitchX
    const y = grid.top + row * grid.pitchY
    if (!barcodes.has(item.code)) barcodes.set(item.code, articleBarcode(item.code))
    const barcode = barcodes.get(item.code)
    if (!barcode) throw new Error(`Código no imprimible: ${item.code}`)
    if (opts.format === 'counter') {
      counterLabel(pages[pageIndex], item, barcode, x, y)
    } else {
      pages[pageIndex].push({ kind: 'outline', x, y, w: grid.width, h: grid.height })
      if (opts.format === 'roll50x50') squareLabel(pages[pageIndex], item, barcode, x, y, opts)
      else compactLabel(pages[pageIndex], item, barcode, x, y, opts)
    }
  })
  return pages
}

// ---------- SVG e impresión ----------

const SVG_FONTS: Record<FontName, string> = {
  regular: 'Helvetica, Arial, sans-serif',
  bold: 'Helvetica, Arial, sans-serif',
  mono: '"Courier New", Courier, monospace',
}

const escapeXml = (text: string) =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

/**
 * Una página como SVG. `outlines` dibuja el borde de las etiquetas adhesivas,
 * que sirve en la vista previa pero no se imprime.
 */
export function sheetToSvg(shapes: Shape[], size: PageSize, outlines: boolean): string {
  const parts: string[] = []
  for (const s of shapes) {
    if (s.kind === 'bar') {
      parts.push(`<rect x="${num(s.x)}" y="${num(s.y)}" width="${num(s.w)}" height="${num(s.h)}"/>`)
    } else if (s.kind === 'cut' || (s.kind === 'outline' && outlines)) {
      const dash = s.kind === 'cut' ? ' stroke-dasharray="2 3"' : ' rx="6"'
      parts.push(
        `<rect x="${num(s.x)}" y="${num(s.y)}" width="${num(s.w)}" height="${num(s.h)}" fill="none" stroke="#c8c8c8" stroke-width="0.6"${dash}/>`
      )
    } else if (s.kind === 'text') {
      const anchor = s.align === 'center' ? 'middle' : s.align === 'right' ? 'end' : 'start'
      // textLength: mismo ancho que en el PDF, aunque la computadora no tenga Helvetica.
      parts.push(
        `<text x="${num(s.x)}" y="${num(s.y)}" font-size="${num(s.size)}" font-family='${SVG_FONTS[s.font]}' ` +
          `font-weight="${s.font === 'regular' ? 400 : 700}" text-anchor="${anchor}" ` +
          `textLength="${num(textWidth(s.text, s.font, s.size))}" lengthAdjust="spacingAndGlyphs">${escapeXml(s.text)}</text>`
      )
    }
  }
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${num(size.width)} ${num(size.height)}" ` +
    `width="100%" height="100%" shape-rendering="crispEdges">${parts.join('')}</svg>`
  )
}

/**
 * Documento para imprimir desde el navegador. `@page` le pasa a Chrome el tamaño
 * exacto y margen cero, y en este modo imprime al 100 %: así el cuadro de
 * impresión abre ya configurado. Una página por hoja (o por etiqueta, en rollo).
 */
export function sheetToPrintHtml(pages: Shape[][], size: PageSize, title: string): string {
  const w = `${num(size.width)}pt`
  const h = `${num(size.height)}pt`
  const body = pages.map((shapes) => `<div class="page">${sheetToSvg(shapes, size, false)}</div>`).join('')
  return (
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeXml(title)}</title><style>` +
    `@page{size:${w} ${h};margin:0}` +
    `html,body{margin:0;padding:0;background:#fff}` +
    `.page{width:${w};height:${h};overflow:hidden;break-after:page;page-break-after:always}` +
    `.page:last-child{break-after:auto;page-break-after:auto}` +
    `svg{display:block}` +
    `</style></head><body>${body}</body></html>`
  )
}

// ---------- PDF ----------

const FONT_RESOURCES: Record<FontName, { id: string; base: string }> = {
  regular: { id: 'F1', base: 'Helvetica' },
  bold: { id: 'F2', base: 'Helvetica-Bold' },
  mono: { id: 'F3', base: 'Courier-Bold' },
}

// Caracteres de WinAnsiEncoding fuera de Latin-1 que pueden salir en una etiqueta.
const WIN_ANSI_EXTRA: Record<string, number> = { '…': 0x85, '–': 0x96, '—': 0x97, '‘': 0x91, '’': 0x92, '“': 0x93, '”': 0x94, '€': 0x80 }

function pdfString(text: string): string {
  let out = '('
  for (const ch of text) {
    let code = WIN_ANSI_EXTRA[ch] ?? ch.charCodeAt(0)
    if (code > 255 || (code >= 0x80 && code < 0xa0 && !(ch in WIN_ANSI_EXTRA))) code = 0x3f // '?'
    if (ch === '(' || ch === ')' || ch === '\\') out += '\\' + ch
    else if (code < 32 || code > 126) out += '\\' + code.toString(8).padStart(3, '0')
    else out += String.fromCharCode(code)
  }
  return out + ')'
}


function pageContent(shapes: Shape[], pageHeight: number): string {
  const ops: string[] = ['0 g']
  for (const s of shapes) {
    if (s.kind === 'bar') {
      ops.push(`${num(s.x)} ${num(pageHeight - s.y - s.h)} ${num(s.w)} ${num(s.h)} re f`)
    } else if (s.kind === 'cut') {
      ops.push(`q 0.8 G 0.5 w [2 3] 0 d ${num(s.x)} ${num(pageHeight - s.y - s.h)} ${num(s.w)} ${num(s.h)} re S Q`)
    } else if (s.kind === 'text') {
      const width = textWidth(s.text, s.font, s.size)
      const x = s.align === 'center' ? s.x - width / 2 : s.align === 'right' ? s.x - width : s.x
      ops.push(`BT /${FONT_RESOURCES[s.font].id} ${num(s.size)} Tf ${num(x)} ${num(pageHeight - s.y)} Td ${pdfString(s.text)} Tj ET`)
    }
  }
  return ops.join('\n')
}

function latin1(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length)
  for (let i = 0; i < text.length; i++) bytes[i] = text.charCodeAt(i) & 0xff
  return bytes
}

/** PDF con las páginas dadas, del tamaño del formato. Barras vectoriales: nítidas a cualquier resolución. */
export function sheetToPdf(pages: Shape[][], title: string, size: PageSize = PAGE_SIZES.avery5160): Uint8Array {
  const chunks: Uint8Array[] = []
  const offsets: number[] = []
  let length = 0
  const push = (part: string | Uint8Array) => {
    const bytes = typeof part === 'string' ? latin1(part) : part
    chunks.push(bytes)
    length += bytes.length
  }
  const object = (id: number, body: string | Uint8Array[]) => {
    offsets[id] = length
    push(`${id} 0 obj\n`)
    if (typeof body === 'string') push(body)
    else body.forEach(push)
    push('\nendobj\n')
  }

  const fontIds = { regular: 3, bold: 4, mono: 5 }
  const firstPageId = 6
  const pageIds = pages.map((_, i) => firstPageId + i * 2)
  const infoId = firstPageId + pages.length * 2

  push('%PDF-1.4\n%\xe2\xe3\xcf\xd3\n')
  object(1, '<< /Type /Catalog /Pages 2 0 R >>')
  object(2, `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pages.length} >>`)
  for (const font of Object.keys(fontIds) as FontName[]) {
    object(fontIds[font], `<< /Type /Font /Subtype /Type1 /BaseFont /${FONT_RESOURCES[font].base} /Encoding /WinAnsiEncoding >>`)
  }
  const fonts = (Object.keys(fontIds) as FontName[]).map((f) => `/${FONT_RESOURCES[f].id} ${fontIds[f]} 0 R`).join(' ')
  pages.forEach((shapes, i) => {
    const pageId = pageIds[i]
    object(
      pageId,
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${num(size.width)} ${num(size.height)}] ` +
        `/Resources << /Font << ${fonts} >> >> /Contents ${pageId + 1} 0 R >>`
    )
    const stream = zlibSync(latin1(pageContent(shapes, size.height)))
    object(pageId + 1, [latin1(`<< /Length ${stream.length} /Filter /FlateDecode >>\nstream\n`), stream, latin1('\nendstream')])
  })
  object(infoId, `<< /Title ${pdfString(title)} /Producer (InvoSloth) >>`)

  const xref = length
  const objectCount = infoId + 1
  let table = `xref\n0 ${objectCount}\n0000000000 65535 f \n`
  for (let id = 1; id < objectCount; id++) table += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`
  push(table)
  push(`trailer\n<< /Size ${objectCount} /Root 1 0 R /Info ${infoId} 0 R >>\nstartxref\n${xref}\n%%EOF\n`)

  const out = new Uint8Array(length)
  let offset = 0
  for (const chunk of chunks) {
    out.set(chunk, offset)
    offset += chunk.length
  }
  return out
}
